import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Smartphone, MessageSquare, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import GlobalToaster from "@/components/GlobalToaster";

/**
 * 微信登录后手机号验证页面
 *
 * 功能：
 * - 输入手机号
 * - 发送验证码
 * - 验证手机号
 * - 同意服务协议
 */
export default function PhoneVerification() {
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // 发送验证码
  const handleSendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      toast.error("请输入正确的手机号");
      return;
    }

    setIsSendingCode(true);

    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";

      const response = await fetch("/users/send_verification_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("验证码已发送");
        // 开始倒计时
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error(data.message || "发送失败，请稍后重试");
      }
    } catch {
      toast.error("网络错误，请稍后重试");
    } finally {
      setIsSendingCode(false);
    }
  };

  // 提交验证
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!/^1[3-9]\d{9}$/.test(phone)) {
      toast.error("请输入正确的手机号");
      return;
    }

    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("请输入6位验证码");
      return;
    }

    if (!agreeTerms) {
      toast.error("请阅读并同意服务协议和隐私政策");
      return;
    }

    setIsLoading(true);

    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";

      const response = await fetch("/users/verify_phone", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          phone,
          verification_code: verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("手机号验证成功");
        // 跳转到首页
        setTimeout(() => {
          window.location.href = "/app";
        }, 1000);
      } else {
        toast.error(data.error || "验证失败，请重试");
      }
    } catch {
      toast.error("网络错误，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-4">
          {/* 微信验证标识 */}
          <div className="mx-auto flex items-center justify-center gap-2 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-4 py-1.5 rounded-full text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            微信账号验证
          </div>

          <div className="mx-auto w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
            <Smartphone className="w-7 h-7 text-primary" />
          </div>

          <CardTitle className="text-2xl">完成手机验证</CardTitle>
          <CardDescription className="text-base">
            您已通过微信成功登录，请验证您的手机号以完成全部流程
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* 提示信息 */}
          <div className="flex items-start gap-3 p-4 mb-6 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
            <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              绑定手机号后，您可以使用微信快速登录，也可以使用手机号登录
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 手机号输入 */}
            <div className="space-y-2">
              <Label htmlFor="phone">手机号码</Label>
              <div className="flex gap-2">
                <Input
                  id="phone"
                  type="tel"
                  placeholder="请输入手机号码"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-11 flex-1"
                  maxLength={11}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={isSendingCode || countdown > 0 || isLoading}
                  className="h-11 px-4 whitespace-nowrap"
                >
                  {countdown > 0
                    ? `${countdown}s`
                    : isSendingCode
                      ? "发送中..."
                      : "获取验证码"}
                </Button>
              </div>
            </div>

            {/* 验证码输入 */}
            <div className="space-y-2">
              <Label htmlFor="verification_code">验证码</Label>
              <Input
                id="verification_code"
                type="text"
                placeholder="请输入6位验证码"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, ""))
                }
                required
                disabled={isLoading}
                className="h-11"
                maxLength={6}
              />
            </div>

            {/* 同意协议 */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="agree_terms"
                checked={agreeTerms}
                onCheckedChange={(checked) => setAgreeTerms(checked === true)}
                disabled={isLoading}
                className="mt-0.5"
              />
              <label
                htmlFor="agree_terms"
                className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
              >
                我已阅读并同意
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-primary hover:underline mx-0.5"
                >
                  《服务协议》
                </Link>
                与
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-primary hover:underline mx-0.5"
                >
                  《隐私政策》
                </Link>
              </label>
            </div>

            {/* 提交按钮 */}
            <Button
              type="submit"
              className="w-full h-11 bg-green-600 hover:bg-green-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  验证中...
                </>
              ) : (
                "完成验证"
              )}
            </Button>
          </form>

          {/* 底部帮助 */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            遇到问题？
            <a href="/contact" className="text-primary hover:underline ml-1">
              联系客服
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
